import { Fragment, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { api } from '../api/client'
import { Face, FacePile } from '../components/primitives'
import { radius } from '../components/primitives/shape'
import { displayName, parseBody } from '../room/format'
import { AgentName } from '../room/Stream'
import type { Agent, Machine, RunTargets } from '../api/types'

/**
 * The composer shows intent before sending.
 *
 * Typing `@` opens an autocomplete over the roster; once someone is named the
 * chip flips from *Room · everyone* to *Named · atlas* and the border warms.
 * The client only previews this — the server parses mentions on the write path
 * and is the authority. If they ever disagree, the server is right.
 */
export interface ComposerProps {
  roster: Agent[]
  nobodyRunning: boolean
  /** `alsoPost` is whether "Also send to #room" was ticked, and always false in a room. */
  onSend: (body: string, alsoPost: boolean) => Promise<void> | void
  /** Below 1060px the chip's explanatory hint is dropped so the row cannot wrap. */
  narrow?: boolean
  /**
   * A name to append to the draft, from "Mention in the room" on a profile.
   *
   * Passed in rather than written into the textarea from outside: the draft
   * lives here, and a caller reaching into the DOM to set it would leave this
   * component's idea of the draft and the box disagreeing.
   */
  mentionRequest?: { name: string; nonce: number } | null
  onMentionInserted?: () => void
  /** The room the commands act on. Without it the box only posts. */
  channelId?: string
  roomName?: string
  onOpenStatus?: () => void
  onOpenClaims?: () => void
  onOpenLessons?: () => void
  onOpenSetup?: () => void
  /** The header's Start agent: put the `/agent` chip in front of the draft. */
  agentRequested?: boolean
  onAgentChipShown?: () => void
  /**
   * A direct message to `agent` instead of the room: no commands and no chip,
   * and "Also send to #`alsoTo`" offered when the agent has a room.
   */
  direct?: { agent: string; alsoTo: string | null }
  /** A reply in a thread instead: "Also send to #`room`", and who it reaches without a tag. */
  thread?: { room: string; reaches: string[] }
}

/** The server's own limit on a task, so a long one stops here rather than at a 422. */
const MAX_TASK_LENGTH = 8000
const COMMAND_QUERY = /^\/(\S*)$/
const AGENT_TYPED = /^\/agent\s+/

const MENU_STYLE: CSSProperties = {
  position: 'absolute', left: 18, right: 18, bottom: 'calc(100% - 6px)',
  background: 'rgba(20,20,20,.86)', backdropFilter: 'blur(24px)',
  border: '1px solid var(--border-strong)', borderRadius: radius.lg,
  padding: 6, zIndex: 9,
  // Never more than half the screen. A fixed 250px is a third of a
  // desktop window and most of a phone with a keyboard open, where
  // the list covered the draft it was completing.
  maxHeight: 'min(250px, 40vh)', overflowY: 'auto',
  overscrollBehavior: 'contain',
  listStyle: 'none', margin: 0, boxShadow: 'var(--shadow-panel)',
}

const TOOL_STYLE: CSSProperties = {
  width: 28, height: 28, flex: 'none', borderRadius: radius.sm, cursor: 'pointer',
  font: '400 14px var(--font-machine)', color: 'var(--text-faint)',
}

/**
 * A handle starts a word and never follows one, so an email is not a mention.
 *
 * The slash group is the whole point: an agent is named after its branch, and
 * every branch looks like `claude/policy-partner-coverage-5d84df`. Stopping at
 * the slash matched `claude` against the roster, found nothing, and told
 * someone who had just tagged two agents that nobody was named. A slash must be
 * followed by more handle, so a trailing one never joins the name.
 */
const MENTION_PATTERN = /(?<![\w.])@([a-z][a-z0-9_-]*(?:\/[a-z0-9_-]+)*)/gi
const TRAILING_HANDLE = /(?<![\w.])@([a-z0-9_\-/]*)$/i

export function mentionsIn(text: string, known: string[]): string[] {
  const names = new Set(known.map((name) => name.toLowerCase()))
  const found: string[] = []
  for (const match of text.matchAll(MENTION_PATTERN)) {
    const handle = match[1].toLowerCase()
    if (names.has(handle) && !found.includes(handle)) found.push(handle)
  }
  return found
}

/**
 * Whether `@here` should be offered for what has been typed.
 *
 * It was in no list at all, so the one handle that reaches every agent in the
 * room could only be used by somebody who already knew it existed — and a
 * broadcast nobody can discover is a broadcast that gets replaced by four
 * separate messages.
 *
 * Matched on prefix only, unlike an agent name. `here` is five characters and
 * the whole of it; the substring ranking exists because branch names all begin
 * `claude/` and what a person remembers is the middle, which is not a problem a
 * five-letter word has.
 */
export function suggestsEveryone(typed: string): boolean {
  return BROADCAST_HANDLE.startsWith(typed.trim().toLowerCase())
}

/** The handle that reaches every agent in the room. `core.domain` owns the
 *  same constant on the Python side; this is the one place the web repeats it. */
export const BROADCAST_HANDLE = 'here'

/**
 * The roster, filtered and ranked against what has been typed after the `@`.
 *
 * Prefix matching is useless on names that all begin `claude/`: what a person
 * remembers is the middle of the branch — "policy", "pams" — and typing that
 * found nothing at all. Ranking keeps the obvious answer on top anyway:
 *
 *   0. the name starts with what was typed
 *   1. a segment of it does, splitting on `/`, `-` and spaces
 *   2. it appears anywhere in the name
 *
 * The nickname is matched the same way, so `@clara` finds "Clara Cookies".
 * Ties keep roster order, which is how the room already sorts agents.
 */
export function suggestAgents(roster: Agent[], typed: string): Agent[] {
  const needle = typed.trim().toLowerCase()
  if (!needle) return roster

  const rankOf = (name: string | null) => {
    const text = name?.toLowerCase() ?? ''
    if (text.startsWith(needle)) return 0
    if (text.split(/[/\s-]/).some((segment) => segment.startsWith(needle))) return 1
    return text.includes(needle) ? 2 : Infinity
  }
  const ranked: { agent: Agent; rank: number; order: number }[] = []
  roster.forEach((agent, order) => {
    const rank = Math.min(rankOf(agent.name), rankOf(agent.nickname))
    if (rank < Infinity) ranked.push({ agent, rank, order })
  })

  return ranked
    .sort((left, right) => left.rank - right.rank || left.order - right.order)
    .map((entry) => entry.agent)
}

export function Composer({
  roster, nobodyRunning, onSend, narrow,
  mentionRequest, onMentionInserted, channelId, roomName,
  onOpenStatus, onOpenClaims, onOpenLessons, onOpenSetup, agentRequested, onAgentChipShown,
  direct, thread,
}: ComposerProps) {
  const [draft, setDraft] = useState('')
  const [alsoPost, setAlsoPost] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [commandsOpen, setCommandsOpen] = useState(false)
  const [highlighted, setHighlighted] = useState(0)
  /** Whether the `/agent` chip is in front of the draft, which makes the draft a task. */
  const [agent, setAgent] = useState(false)
  // Kept with the room they were read for, so a room change never starts an
  // agent on the last room's machine while the new list is on its way.
  const [targets, setTargets] = useState<{ channelId: string; found: RunTargets } | null>(null)
  const [setupMachines, setSetupMachines] = useState<Machine[]>([])
  const [machineId, setMachineId] = useState('')
  const [choosingMachine, setChoosingMachine] = useState(false)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const fieldRef = useRef<HTMLDivElement>(null)
  const ids = useId()

  function startAgent() {
    setAgent(true)
    inputRef.current?.focus()
  }

  useEffect(() => {
    if (!agentRequested || !channelId) return
    startAgent()
    onAgentChipShown?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentRequested])

  useEffect(() => {
    if (!agent || !channelId) return
    let current = true
    api.runTargets(channelId).then(
      (found) => {
        if (!current) return
        setTargets({ channelId, found })
        setMachineId((found.machines.find((machine) => machine.default) ?? found.machines[0])?.id ?? '')
      },
      (failure: unknown) => {
        if (current) setError(failure instanceof Error ? failure.message : 'could not find your machines')
      },
    )
    // The mode is a detail of the hint, which leaves it out when this read fails.
    api.mySetup().then((setup) => { if (current) setSetupMachines(setup.machines) }, () => undefined)
    return () => { current = false }
  }, [agent, channelId])

  // Keyed by nonce, so mentioning the same agent twice in a row still appends
  // twice. Naming alone would look unchanged to the effect and do nothing.
  const nonce = mentionRequest?.nonce
  const requested = mentionRequest?.name
  useEffect(() => {
    if (!requested) return
    setDraft((previous) => `${previous.replace(/\s*$/, previous ? ' ' : '')}@${requested} `)
    inputRef.current?.focus()
    onMentionInserted?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requested, nonce])

  // The box is as tall as the draft, up to the cap in `base.css`, and only
  // scrolls past that. A one-row textarea with nothing growing it put a long
  // draft behind its own scrollbar, two lines at a time, so nobody could read
  // back what they were about to send.
  //
  // Collapsing to `auto` is what lets it shrink when lines are deleted or the
  // draft is sent, and the field is held at its height while that happens.
  // Otherwise the stream above grows into the collapse for the one layout the
  // measurement forces, clamps its scroll position, and a reader who was at
  // the newest message is no longer at it once the box comes back.
  //
  // It scrolls only once the cap is reached. Line heights are fractional, so a
  // box sized to the rounded `scrollHeight` can be a fraction of a pixel short
  // of its text, and that fraction was enough to draw a scrollbar.
  const fitToDraft = useCallback(() => {
    const box = inputRef.current
    const field = fieldRef.current
    if (!box || !field) return
    field.style.height = `${field.getBoundingClientRect().height}px`
    box.style.overflowY = 'hidden'
    box.style.height = 'auto'
    const content = box.scrollHeight
    box.style.height = `${content}px`
    box.style.overflowY = content > box.clientHeight ? 'auto' : 'hidden'
    field.style.height = ''
  }, [])

  // Before paint, so no frame shows the new text at the old height.
  useLayoutEffect(fitToDraft, [draft, fitToDraft])

  // The same draft wraps to a different number of lines when the box changes
  // width (a rail dragged, the panel opened beside it), and no
  // keystroke arrives to measure it again. Only the width is answered, because
  // fitting changes the height this observer also reports, and the fit waits
  // a frame because resizing an element from inside its own observer is the
  // loop the browser reports as an error.
  useEffect(() => {
    const box = inputRef.current
    if (!box) return
    let width = box.clientWidth
    let frame = 0
    const observer = new ResizeObserver(() => {
      if (box.clientWidth === width) return
      width = box.clientWidth
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(fitToDraft)
    })
    observer.observe(box)
    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
    }
  }, [fitToDraft])

  const names = useMemo(() => roster.map((agent) => agent.name), [roster])
  const named = mentionsIn(draft, names)
  const directive = named.length > 0
  const label = direct ? `Message ${direct.agent}` : thread ? 'Reply to this thread' : 'Say something to the room'
  const alsoTo = direct ? direct.alsoTo : thread?.room

  // Escape closes the roster for the draft it was offered on; typing brings it back.
  const [mentionsDismissedFor, setMentionsDismissedFor] = useState<string | null>(null)
  const partial = draft === mentionsDismissedFor ? null : TRAILING_HANDLE.exec(draft)
  const suggestions = partial ? suggestAgents(roster, partial[1]) : []
  // Offered on its own, even where no agent name matches: `@he` should reach it.
  const everyone = partial !== null && suggestsEveryone(partial[1])
  const running = useMemo(() => roster.filter((agent) => agent.online), [roster])

  const offered = targets && targets.channelId === channelId ? targets.found : null
  const machines = offered?.machines ?? []
  const chosen = machines.find((machine) => machine.id === machineId)
  const known = setupMachines.find((machine) => machine.id === chosen?.id)
  const mode = known?.permission_mode ?? known?.permission_mode_default ?? null
  const canSend = draft.trim() !== '' && !sending && (!agent || chosen !== undefined)

  const commands = channelId ? [
    { name: 'agent', description: 'Start an agent on your machine with this as its task', run: startAgent },
    { name: 'status', description: 'Set what the room is working on', run: onOpenStatus },
    { name: 'files', description: 'See who is changing which files', run: onOpenClaims },
    { name: 'lessons', description: "Search the organisation's lessons", run: onOpenLessons },
  ] : []
  const query = COMMAND_QUERY.exec(draft)?.[1].toLowerCase() ?? ''
  const shownCommands = commandsOpen ? commands.filter((command) => command.name.startsWith(query)) : []

  function pickCommand(command: (typeof commands)[number]) {
    setCommandsOpen(false)
    if (COMMAND_QUERY.test(draft)) setDraft('')
    command.run?.()
  }

  function openCommands() {
    if (draft === '') setDraft('/')
    setCommandsOpen(true)
    setHighlighted(0)
    inputRef.current?.focus()
  }

  async function send() {
    const body = draft.trim()
    const run = agent && channelId && chosen ? { channelId, machine_id: chosen.id } : null
    if (!canSend || (agent && !run)) return
    setSending(true)
    setError(null)
    try {
      if (run) await api.startRun(run.channelId, { task: body, machine_id: run.machine_id })
      else await onSend(body, alsoPost && Boolean(alsoTo))
      setDraft('')
      setAgent(false)
      setAlsoPost(false)
    } catch (failure) {
      // An explicit write that failed must say so. Clearing the box would
      // leave someone believing they had sent something they had not.
      setError(failure instanceof Error ? failure.message : 'not sent')
    } finally {
      setSending(false)
    }
  }

  function pick(name: string) {
    setDraft(draft.replace(TRAILING_HANDLE, `@${name} `))
    inputRef.current?.focus()
  }

  return (
    <div
      className="sp-composer"
      style={{ flex: 'none', position: 'relative' }}
      onKeyDown={(event) => {
        if (event.key !== 'Escape' || !choosingMachine) return
        event.stopPropagation()
        setChoosingMachine(false)
        inputRef.current?.focus()
      }}
    >
      {shownCommands.length > 0 && (
        <ul id={`${ids}-commands`} role="listbox" aria-label="Commands" style={MENU_STYLE}>
          {shownCommands.map((command, index) => (
            <li
              key={command.name}
              id={`${ids}-${command.name}`}
              role="option"
              aria-selected={index === highlighted}
              // The box keeps focus, so the keys go on working after a hover.
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => setHighlighted(index)}
              onClick={() => pickCommand(command)}
              style={{
                display: 'flex', alignItems: 'baseline', gap: 16, padding: '9px 10px', cursor: 'pointer',
                borderRadius: radius.sm, background: index === highlighted ? 'var(--panel-strong)' : 'transparent',
              }}
            >
              <span style={{ width: 84, flex: 'none', font: '700 12.5px var(--font-machine)', color: 'var(--text)' }}>
                /{command.name}
              </span>
              <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: 'var(--text-dim)' }}>{command.description}</span>
              {command.name === 'agent' && (
                <span style={{ flex: 'none', fontSize: 11, color: 'var(--text-faint)' }}>{roomName}</span>
              )}
            </li>
          ))}
        </ul>
      )}

      {agent && choosingMachine && (
        <ul role="listbox" aria-label="Machine" style={{ ...MENU_STYLE, left: 'auto', width: 240 }}>
          {machines.map((machine) => (
            <li key={machine.id} role="presentation">
              <button
                type="button"
                role="option"
                aria-selected={machine.id === machineId}
                onClick={() => { setMachineId(machine.id); setChoosingMachine(false) }}
                style={{
                  width: '100%', textAlign: 'left', padding: '8px 10px', borderRadius: radius.sm, cursor: 'pointer',
                  fontSize: 12.5, color: machine.id === machineId ? 'var(--text)' : 'var(--text-dim)',
                }}
              >
                {machine.name}
              </button>
            </li>
          ))}
        </ul>
      )}

      {(everyone || suggestions.length > 0) && (
        <ul role="listbox" aria-label="Mention an agent" style={MENU_STYLE}>
          {everyone && (
            <li key="@here">
              <button
                onClick={() => pick(BROADCAST_HANDLE)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
                  width: '100%', minHeight: 44, cursor: 'pointer', borderRadius: radius.sm,
                  // Set apart from the names below it, because picking this is a
                  // different decision from picking one of them.
                  background: 'var(--accent-wash)',
                  border: '1px solid var(--accent-edge)',
                  marginBottom: suggestions.length ? 6 : 0,
                }}
              >
                {/* The room's own faces rather than an icon: what `@here` means
                    is exactly these agents, and showing them is both the
                    clearest label and an honest count.

                    **Running agents only.** The roster carries every name that
                    has ever spoken here, so on a busy repository this counted
                    seventeen when five were live and twelve were branches that
                    merged a fortnight ago. A number that overstates who is
                    about to be interrupted is worse than no number: `@here` is
                    reserved for the handful of things everybody has to act on,
                    and this is the line that says how much it costs. */}
                {/* No fixed width. Three faces overlapping at 22px are 52px
                    across, and the 26px slot an agent row uses for its single
                    face spilled them over the text beside it. This row is a
                    different kind of row — its own ground, its own border, its
                    own chip — so it may be the width of what it holds. */}
                <span style={{ flex: 'none', display: 'flex', alignItems: 'center' }}>
                  {running.length > 0
                    ? <FacePile names={running.map((agent) => agent.name)} limit={3} size={22} />
                    : <Face name="here" size={26} avatarKey={null} />}
                </span>
                <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                  <span style={{ display: 'block', fontSize: 12.5, fontWeight: 800, color: 'var(--accent-bright)' }}>
                    @here
                  </span>
                  <span style={{ display: 'block', fontSize: 10.5, color: 'var(--text-dim)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {running.length === 0
                      ? 'nobody is running here to act on it'
                      : running.length === 1
                        ? 'the one agent running here has to act on it'
                        : `all ${running.length} agents running here have to act on it`}
                  </span>
                </span>
                <span style={{ font: '700 9px var(--font-interface)', letterSpacing: '.16em', textTransform: 'uppercase', color: 'var(--accent)', flex: 'none' }}>
                  everyone
                </span>
              </button>
            </li>
          )}
          {suggestions.map((agent) => (
            <li key={agent.name}>
              <button
                onClick={() => pick(agent.name)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', width: '100%', minHeight: 44, cursor: 'pointer', borderRadius: radius.sm }}
              >
                <Face
                  name={agent.name}
                  size={26}
                  avatarKey={agent.avatar_key}
                  online={agent.online}
                />
                <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                  <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700 }}>
                    <AgentName handle={agent.name} nickname={agent.nickname} />
                  </span>
                  <span style={{ display: 'block', fontSize: 10.5, color: 'var(--text-faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {agent.summary ?? ''}
                  </span>
                </span>
                {/* Whose agent, so naming the right one does not need the roster. */}
                {agent.owner && (
                  <span style={{ font: '400 10px var(--font-machine)', color: 'var(--text-faint)', flex: 'none' }}>
                    {agent.owner.toLowerCase()}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      {nobodyRunning && (
        <p
          style={{
            marginBottom: 10, padding: '11px 14px', borderRadius: radius.md,
            background: 'var(--panel)', border: '1px solid var(--border)',
            fontSize: 12.5, lineHeight: 1.5, color: 'var(--text-dim)',
          }}
        >
          No agent is running here. What you post waits in the room and is the
          first thing an agent sees when one starts.
          {channelId && (
            <>
              {' '}
              <button
                type="button"
                onClick={startAgent}
                style={{ color: 'var(--accent)', fontWeight: 600, cursor: 'pointer' }}
              >
                Start one
              </button>
            </>
          )}
        </p>
      )}

      {error && (
        <p role="alert" style={{ marginBottom: 10, fontSize: 12.5, color: 'var(--deletions)' }}>
          {error}
        </p>
      )}

      <div
        ref={fieldRef}
        className="sp-field sp-composer-field"
        style={{
          background: 'var(--panel-strong)',
          border: `1px solid ${directive ? 'var(--accent-edge)' : 'var(--border-strong)'}`,
          borderRadius: radius.xl,
          transition: 'border-color .2s, box-shadow .2s',
          // The chip and the box share the first line; the row wraps under them.
          display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', columnGap: 7,
        }}
      >
        {agent && (
          <span style={{ flex: 'none', font: '500 var(--size-body)/var(--line-body) var(--font-machine)', color: 'var(--accent)' }}>
            /agent
          </span>
        )}
        <textarea
          ref={inputRef}
          rows={1}
          value={draft}
          maxLength={agent ? MAX_TASK_LENGTH : undefined}
          aria-label={label}
          aria-expanded={shownCommands.length > 0}
          aria-controls={shownCommands.length > 0 ? `${ids}-commands` : undefined}
          aria-activedescendant={shownCommands.length > 0 ? `${ids}-${shownCommands[highlighted].name}` : undefined}
          aria-describedby={agent ? `${ids}-hint` : undefined}
          placeholder={agent ? 'What should it do?' : thread ? 'Reply…' : label}
          onChange={(event) => {
            const next = event.target.value
            if (channelId && !agent && AGENT_TYPED.test(next)) {
              setAgent(true)
              setCommandsOpen(false)
              setDraft(next.replace(AGENT_TYPED, ''))
              return
            }
            setCommandsOpen(Boolean(channelId) && !agent && COMMAND_QUERY.test(next) && (commandsOpen || draft === ''))
            setHighlighted(0)
            setDraft(next)
          }}
          onKeyDown={(event) => {
            if (shownCommands.length > 0) {
              if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault()
                const step = event.key === 'ArrowDown' ? 1 : shownCommands.length - 1
                setHighlighted((highlighted + step) % shownCommands.length)
                return
              }
              if (event.key === 'Enter' || event.key === 'Tab') {
                event.preventDefault()
                pickCommand(shownCommands[highlighted])
                return
              }
              if (event.key === 'Escape') {
                // Closing the menu is all this Escape does, not the room's panel too.
                event.stopPropagation()
                setCommandsOpen(false)
                return
              }
            }
            if (event.key === 'Escape' && (everyone || suggestions.length > 0)) {
              // Closing the roster is all this Escape does, not the room's panel too.
              event.stopPropagation()
              setMentionsDismissedFor(draft)
              return
            }
            if (event.key === 'Backspace' && agent && draft === '') {
              setAgent(false)
              setChoosingMachine(false)
              setError(null)
            }
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault()
              void send()
            }
          }}
          style={{
            flex: 1, minWidth: 0, resize: 'none', fontFamily: 'var(--font-human)',
            fontSize: 'var(--size-body)', lineHeight: 'var(--line-body)',
            color: '#fff',
          }}
        />
        {alsoTo && (
          <label style={{ flexBasis: '100%', display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, fontSize: 12.5, color: 'var(--text-dim)', cursor: 'pointer' }}>
            <input type="checkbox" checked={alsoPost} onChange={(event) => setAlsoPost(event.target.checked)} />
            Also send to #{alsoTo}
          </label>
        )}
        {thread && thread.reaches.length > 0 && (
          <p style={{ flexBasis: '100%', margin: '4px 0 0', font: '400 11.5px/1.5 var(--font-interface)', color: 'var(--text-meta)', overflowWrap: 'anywhere' }}>
            Reaches{' '}
            {thread.reaches.map((handle, index) => {
              const nickname = roster.find((agent) => agent.name.toLowerCase() === handle)?.nickname
              return (
                <Fragment key={handle}>
                  {index === 0 ? '' : index === thread.reaches.length - 1 ? ' and ' : ', '}
                  <span
                    title={handle}
                    style={{ fontWeight: 600, color: 'var(--accent)', ...(!nickname && { fontFamily: 'var(--font-machine)', fontSize: 11 }) }}
                  >
                    {displayName(handle, nickname)}
                  </span>
                </Fragment>
              )
            })}
            , {thread.reaches.length === 1 ? 'who is' : 'who are'} in this thread
          </p>
        )}
        <div style={{ flexBasis: '100%', display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, flexWrap: 'nowrap', minWidth: 0 }}>
          {channelId && (
            <button type="button" aria-label="Commands" onClick={openCommands} className="sp-tap" style={TOOL_STYLE}>/</button>
          )}
          <button type="button" aria-label="Mention someone" className="sp-tap" style={TOOL_STYLE}
                  onClick={() => { setDraft(`${draft.replace(/\s*$/, draft ? ' ' : '')}@`); inputRef.current?.focus() }}>
            @
          </button>
          <span
            data-directive={directive ? 'yes' : 'no'}
            style={{
              display: agent || direct || thread ? 'none' : 'flex', alignItems: 'center', gap: 8, borderRadius: radius.sm, marginLeft: 4,
              padding: '4px 11px',
              background: directive ? 'var(--accent-wash)' : 'var(--panel-strong)',
              color: directive ? 'var(--accent-bright)' : 'var(--text-dim)',
              fontSize: 9.5, fontWeight: 700, letterSpacing: '.16em', textTransform: 'uppercase',
              // Names a directive carries can be long — `claude/policy-…` — and
              // the chip is a summary, not the list. It gives up its width
              // before the send button does.
              minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}
          >
            {directive ? `Named · ${named.join(', ')}` : 'Room · everyone'}
          </span>
          <span
            style={{
              display: narrow || agent || direct || thread ? 'none' : 'inline',
              font: '400 11px var(--font-machine)', color: 'var(--text-faint)',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0,
            }}
          >
            {directive
              ? `${Math.max(0, roster.length - named.length)} others see it as context`
              : 'nobody is named'}
          </span>
          <span style={{ flex: 1 }} />
          {direct && (
            <span style={{ font: '400 11.5px var(--font-interface)', color: 'var(--text-faint)', whiteSpace: 'nowrap' }}>
              {narrow ? 'private' : 'private · reaches the session now'}
            </span>
          )}
          {agent && (
            <span id={`${ids}-hint`} style={{ minWidth: 0, textAlign: 'right', font: '400 11.5px/1.4 var(--font-interface)', color: 'var(--text-faint)' }}>
              {offered?.reason ? (
                <>
                  {parseBody(offered.reason).map((part, index) =>
                    part.kind === 'code'
                      ? <code key={index} style={{ fontFamily: 'var(--font-machine)', color: 'var(--text-dim)' }}>{part.text}</code>
                      : <span key={index}>{part.text}</span>)}
                  {' '}
                  <button type="button" onClick={onOpenSetup} style={{ color: 'var(--accent)', cursor: 'pointer' }}>
                    Set up a runner
                  </button>
                </>
              ) : chosen ? (
                <>
                  starts on {chosen.name}{mode && ` in ${mode}`} ·{' '}
                  <button
                    type="button"
                    onClick={() => (machines.length > 1 ? setChoosingMachine(!choosingMachine) : onOpenSetup?.())}
                    style={{ color: 'var(--accent)', cursor: 'pointer' }}
                  >
                    change
                  </button>
                </>
              ) : 'finding your machines'}
            </span>
          )}
          <button
            type="button"
            onClick={() => void send()}
            disabled={!canSend}
            aria-label="Send"
            className="sp-tap"
            style={{
              width: 30, height: 30, flex: 'none', borderRadius: radius.sm, marginLeft: 6,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 14, fontWeight: 700, cursor: canSend ? 'pointer' : 'default',
              background: canSend ? 'var(--text)' : 'var(--panel-hover)',
              color: canSend ? 'var(--ground)' : 'var(--text-faint)',
            }}
          >
            ↑
          </button>
        </div>
      </div>
    </div>
  )
}
